/**
 * PHASE 6 — minimal column-major 4x4 matrix library (WebGL convention).
 *
 * Storage is column-major (m[col*4 + row]) so arrays can be handed straight
 * to uniformMatrix4fv. Only what the renderer needs: identity, perspective,
 * the active rotation matrix of a quaternion, multiplication, and a
 * clip-space transform (no translation/inverse yet).
 *
 * The perspective matrix is chosen so that NDC output agrees exactly with the
 * CPU-side projection from phase 5 (projectDirection): a unit direction d in
 * device frame yields
 *
 *   xNdc = (f/aspect) * d.x / (-d.z),  yNdc = f * d.y / (-d.z)
 *
 * with f = 1/tan(fovYDeg/2) — see tests/mat4.test.ts: "GPU pipeline agrees
 * with the phase-5 Cpu projection".
 */

export type Mat4 = number[]; // 16 entries, column-major

export function identity(): Mat4 {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

export function isIdentity(m: Mat4, tolerance = 1e-9): boolean {
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      if (Math.abs(m[c * 4 + r]! - (r === c ? 1 : 0)) > tolerance) return false;
    }
  }
  return true;
}

/** Multiply a * b (apply b first, then a). Both inputs unchanged. */
export function multiply(a: Mat4, b: Mat4): Mat4 {
  const out: number[] = new Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      out[r + c * 4] =
        a[r]! * b[c * 4]! +
        a[r + 4]! * b[1 + c * 4]! +
        a[r + 8]! * b[2 + c * 4]! +
        a[r + 12]! * b[3 + c * 4]!;
    }
  }
  return out;
}

/**
 * Classic perspective projection:
 *   clip = P * (x, y, z, 1),  NDC = clip.xy / clip.w
 * z in [-near, -far]; clip.w = -z. Aspect is folded into the x scale so the
 * result matches phase-5 projectDirection exactly.
 */
export function perspective(fovYDeg: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(((fovYDeg / 2) * Math.PI) / 180);
  const nf = near - far;
  return [
    f / aspect, 0, 0, 0, // col 0
    0, f, 0, 0, // col 1
    0, 0, (far + near) / nf, -1, // col 2
    0, 0, (2 * far * near) / nf, 0, // col 3
  ];
}

/**
 * Active rotation matrix of a (unit) quaternion: maps v -> q * v * conj(q),
 * i.e. from the quaternion's source frame into its target frame. Used as the
 * view rotation for qCamera (world ENU -> device).
 */
export function rotationFromQuat(q: { x: number; y: number; z: number; w: number }): Mat4 {
  const { x, y, z, w } = q;
  const xx = x * x,
    yy = y * y,
    zz = z * z;
  const xy = x * y,
    xw = x * w,
    xz = x * z;
  const yz = y * z,
    yw = y * w,
    zw = z * w;
  return [
    1 - 2 * (yy + zz), 2 * (xy + zw), 2 * (xz - yw), 0, // col 0
    2 * (xy - zw), 1 - 2 * (xx + zz), 2 * (yz + xw), 0, // col 1
    2 * (xz + yw), 2 * (yz - xw), 1 - 2 * (xx + yy), 0, // col 2
    0, 0, 0, 1, // col 3
  ];
}

export interface ClipPoint {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** Raw (pre-divide) transform of a homogeneous point. */
export function transformClip(m: Mat4, p: readonly [number, number, number, number]): ClipPoint {
  const [x, y, z, w] = p;
  return {
    x: m[0]! * x + m[4]! * y + m[8]! * z + m[12]! * w,
    y: m[1]! * x + m[5]! * y + m[9]! * z + m[13]! * w,
    z: m[2]! * x + m[6]! * y + m[10]! * z + m[14]! * w,
    w: m[3]! * x + m[7]! * y + m[11]! * z + m[15]! * w,
  };
}

/** Transform a direction vector (w = 0, translation-free matrices only). */
export function transformDirection(m: Mat4, v: readonly [number, number, number]): [number, number, number] {
  const c = transformClip(m, [v[0], v[1], v[2], 0]);
  return [c.x, c.y, c.z];
}